import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { FormValues, StepPayment } from '../../../../common/components/StepPayment';
import { Trans } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { OptionsSharesRegistrationDocument } from '../../../OptionsSharesRegistration.graphql';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import {
  GqlCompany,
  GqlOptionsSharesRegistration,
  GqlPaymentAccountKind,
  GqlWarrantProgram,
} from '../../../../../graphql/base-types.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useOsrSetPaymentMutation } from '../../../../../graphql/react-operations';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { captureException } from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import { toDateTimeMaybe } from '@startuptools/common/common';
import { useWarrantProgramSuspenseQuery, WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { GenericError } from '@startuptools/ui/Error';

export const OsrStepPaymentNgWrap = ({ osrId, warrantProgramId }: { osrId: string; warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const {
    data: { warrantProgram },
  } = useWarrantProgramSuspenseQuery({ variables: { companyId, warrantProgramId, getWpErrors: true } });
  const osr = warrantProgram.optionsSharesRegistrations.find(osr => osr.id === osrId);
  if (!osr) {
    return <GenericError />;
  }
  return <OsrStepPayment osr={osr} warrantProgram={warrantProgram} />;
};

export const OsrStepPayment = ({
  osr: {
    id: osrId,
    payBeforeDate,
    paymentAccountKind,
    paymentAccountNumber,
    paymentName,
    ibanNumber,
    ibanBank,
    ibanBic,
    ibanAddress,
  },
  warrantProgram,
}: {
  osr: Pick<
    GqlOptionsSharesRegistration,
    | 'id'
    | 'payBeforeDate'
    | 'paymentAccountKind'
    | 'paymentAccountNumber'
    | 'paymentName'
    | 'ibanNumber'
    | 'ibanAddress'
    | 'ibanBank'
    | 'ibanBic'
  >;
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'shareSubscribeEndDate' | 'shareSubscribeStartDate'> & {
    company: Pick<GqlCompany, 'id' | 'organizationNumber'>;
  };
}) => {
  const companyId = useCompanyId();
  const { stepNext } = useStepper();
  const company = warrantProgram.company;

  const [doSubmit, { loading }] = useOsrSetPaymentMutation({
    refetchQueries: [WarrantProgramDocument, OptionsSharesRegistrationDocument],
    awaitRefetchQueries: true,
  });

  const { control, register, watch, handleSubmit, setValue, reset } = useForm<FormValues>({
    defaultValues: {
      payBeforeDate:
        toDateTimeMaybe(payBeforeDate) ??
        toDateTimeMaybe(warrantProgram.shareSubscribeEndDate) ??
        DateTime.now().startOf('day'),
      paymentAccountKind: paymentAccountKind ?? GqlPaymentAccountKind.Other,
      paymentAccountNumber: paymentAccountNumber ?? '',
      paymentName: paymentName ?? '',
      ibanNumber: ibanNumber ?? '',
      ibanBank: ibanBank ?? '',
      ibanAddress: ibanAddress ?? '',
      ibanBic: ibanBic ?? '',
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ payBeforeDate, ...values }) => {
    try {
      const res = await doSubmit({
        variables: {
          companyId,
          input: { id: osrId, payBeforeDate: payBeforeDate.toUTC(0, { keepLocalTime: true }).toString(), ...values },
        },
      });
      const or = res.data?.osrSetPayment.osr;
      if (or) {
        reset({
          payBeforeDate: or.payBeforeDate ? DateTime.fromISO(or.payBeforeDate) : DateTime.now(),
          paymentAccountKind: or.paymentAccountKind ?? GqlPaymentAccountKind.Other,
          paymentAccountNumber: or.paymentAccountNumber ?? '',
          paymentName: or.paymentName ?? '',
          ibanNumber: or.ibanNumber ?? '',
          ibanBank: or.ibanBank ?? '',
          ibanAddress: or.ibanAddress ?? '',
          ibanBic: or.ibanBic ?? '',
        });
      }
      return stepNext();
    } catch (e) {
      captureException(e);
      toast({
        title: <Trans id="Could not save payment information" />,
        description: (
          <Trans id="Could not save payment information, try again or contact support if problem persists" />
        ),
        severity: 'danger',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Payment Accounts" />
        </Typography>

        <StepPayment
          control={control}
          register={register}
          watch={watch}
          setValue={setValue}
          company={company}
          minDate={{
            date: warrantProgram.shareSubscribeStartDate
              ? DateTime.fromISO(warrantProgram.shareSubscribeStartDate)
              : DateTime.now().startOf('day'),
            severity: 'warning',
            warningText: <Trans id="Warning: Payment due date set to before subscription end" />,
          }}
        />

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
